import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { fDate, fTime } from '@/utils/format-date'
import { Card, CardContent } from '@/components/ui/card'
import { RoosterMatch, RoosterMatchStatus } from '@/types/rooster-match.type'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  MapPin,
  DollarSign,
} from 'lucide-react'

interface RoosterMatchTableProps {
  matches: RoosterMatch[]
  loading: boolean
  onView: (match: RoosterMatch) => void
  onEdit: (match: RoosterMatch) => void
  onApprove: (match: RoosterMatch) => void
  onReject: (match: RoosterMatch) => void
  onDelete: (match: RoosterMatch) => void
}

const getStatusBadge = (status: RoosterMatchStatus) => {
  const statusConfig = {
    [RoosterMatchStatus.OPEN]: {
      label: 'เปิดรับคู่แข่ง',
      variant: 'default' as const,
      icon: Clock,
    },
    [RoosterMatchStatus.PENDING_APPROVAL]: {
      label: 'รอการอนุมัติ',
      variant: 'secondary' as const,
      icon: Clock,
    },
    [RoosterMatchStatus.APPROVED]: {
      label: 'อนุมัติแล้ว',
      variant: 'default' as const,
      icon: CheckCircle,
    },
    [RoosterMatchStatus.REJECTED]: {
      label: 'ปฏิเสธ',
      variant: 'destructive' as const,
      icon: XCircle,
    },
    [RoosterMatchStatus.CANCELLED]: {
      label: 'ยกเลิก',
      variant: 'outline' as const,
      icon: XCircle,
    },
    [RoosterMatchStatus.COMPLETED]: {
      label: 'เสร็จสิ้น',
      variant: 'secondary' as const,
      icon: CheckCircle,
    },
    [RoosterMatchStatus.IN_PROGRESS]: {
      label: 'กำลังแข่งขัน',
      variant: 'default' as const,
      icon: Clock,
    },
  }

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  )
}

export default function RoosterMatchTable({
  matches,
  loading,
  onView,
  onEdit,
  onApprove,
  onReject,
  onDelete,
}: RoosterMatchTableProps) {
  // const [hoveredRow, setHoveredRow] = useState<string | null>(null)

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">กำลังโหลด...</div>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (matches.length === 0) {
    return (
      <Card className="p-0">
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="text-muted-foreground mb-2">
                ไม่พบข้อมูลการแข่งขัน
              </div>
              <div className="text-sm text-muted-foreground">
                ลองปรับเปลี่ยนเงื่อนไขการค้นหาหรือเพิ่มการแข่งขันใหม่
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="p-0">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>รหัสการแข่งขัน</TableHead>
                <TableHead>ไก่เจ้าบ้าน</TableHead>
                <TableHead>ไก่แขก</TableHead>
                <TableHead>สนามแข่งขัน</TableHead>
                <TableHead>เดิมพัน</TableHead>
                <TableHead>วันที่แข่งขัน</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {matches.map((match) => (
                <TableRow
                  key={match.id}
                  className="hover:bg-muted/50"
                  // onMouseEnter={() => setHoveredRow(match.id)}
                  // onMouseLeave={() => setHoveredRow(null)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        #{match.matchNumber || '-'}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {match.rooster1?.name || '-'}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-2">
                      {match.rooster2 ? (
                        <span className="text-sm">
                          {match.rooster2?.name || '-'}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          รอคู่แข่ง
                        </span>
                      )}
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm">
                        {match.arena?.name || '-'}
                      </span>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1">
                      <DollarSign className="h-3 w-3 text-muted-foreground" />
                      <div className="text-sm">
                        {match.actualBetAmount ? (
                          <span className="font-medium">
                            {match.actualBetAmount.toLocaleString()} บาท
                          </span>
                        ) : (
                          <span>
                            {match.minBetAmount.toLocaleString()} -{' '}
                            {match.maxBetAmount.toLocaleString()} บาท
                          </span>
                        )}
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      <div className="text-sm">
                        <div>{fDate(match.matchDate)}</div>
                        <div className="text-xs text-muted-foreground">
                          {fTime(match.matchDate)}
                        </div>
                      </div>
                    </div>
                  </TableCell>

                  <TableCell>{getStatusBadge(match.status)}</TableCell>

                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onView(match)}>
                          <Eye className="mr-2 h-4 w-4" />
                          ดูรายละเอียด
                        </DropdownMenuItem>

                        {match.status ===
                          RoosterMatchStatus.PENDING_APPROVAL && (
                          <>
                            <DropdownMenuItem
                              onClick={() => onApprove(match)}
                              className="text-green-600"
                            >
                              <CheckCircle className="mr-2 h-4 w-4" />
                              อนุมัติ
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => onReject(match)}
                              className="text-red-600"
                            >
                              <XCircle className="mr-2 h-4 w-4" />
                              ปฏิเสธ
                            </DropdownMenuItem>
                          </>
                        )}

                        <DropdownMenuItem onClick={() => onEdit(match)}>
                          <Edit className="mr-2 h-4 w-4" />
                          แก้ไข
                        </DropdownMenuItem>

                        <DropdownMenuItem
                          onClick={() => onDelete(match)}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          ลบ
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
